import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { Schema } from '../../../../amplify/data/resource';
import Button from '../../../components/custom/Button';
import Tabs from '../../../components/custom/Tabs';
import TextField from '../../../components/custom/TextField';
import { formatIsraeliPhoneNumber } from '../../../utils/format/format';

const apiClient = generateClient<Schema>();

interface FormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  userId: string;
  companyId: string;
  bank: string;
  branch: string;
  account: string;
  message: string;
  distributions: {
    bankAccount: string;
    amount: number;
    description: string;
  }[];
}

const Integrated = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      distributions: []
    }
  });

  const [selectedDebtorAccountType, setSelectedDebtorAccountType] = useState<
    'INDIVIDUAL' | 'BUSINESS'
  >('BUSINESS');

  const myTabs = [
    { label: 'BUSINESS', content: 'BUSINESS' as 'INDIVIDUAL' | 'BUSINESS' },
    { label: 'INDIVIDUAL', content: 'INDIVIDUAL' as 'INDIVIDUAL' | 'BUSINESS' },
  ];

  const handleTabChange = (index: number) => {
    setSelectedDebtorAccountType(myTabs[index].content);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "distributions"
  });

  const onSubmit = async (formData: FormValues) => {
    try {
      setLoading(true);

   
      if (!formData.distributions || formData.distributions.length === 0) {
        enqueueSnackbar('יש להוסיף לפחות חלוקה אחת', { variant: 'error' });
        return;
      }

      // Проверяем что все суммы больше 0
      const hasInvalidAmounts = formData.distributions.some(dist => !dist.amount || dist.amount <= 0);
      if (hasInvalidAmounts) {
        enqueueSnackbar('כל הסכומים חייבים להיות גדולים מ-0', { variant: 'error' });
        return;
      }

      
      const formattedPhone = formatIsraeliPhoneNumber(formData.phone);
      console.log('Original phone:', formData.phone, 'Formatted phone:', formattedPhone);

      if (!formattedPhone || !formattedPhone.startsWith('+972')) {
        enqueueSnackbar('מספר טלפון לא תקין לשליחת הודעת WhatsApp', { variant: 'error' });
        return;
      }

      
      const debtorBankAccount = `${formData.bank}-${formData.branch}-${formData.account}`;


      const distributionsWithFloatAmounts = formData.distributions.map(dist => ({
        bankAccount: dist.bankAccount,
        amount: parseFloat(dist.amount.toString()),
        description: dist.description
      }));

      console.log('Distributions before sending:', distributionsWithFloatAmounts);

      
      const paymentResponse = await apiClient.queries.integratedPayment({
        userId: formData.userId,
        companyId: formData.companyId || '',
        debtorAccountType: selectedDebtorAccountType,
        debtorBankAccount: debtorBankAccount,
        distributions: JSON.stringify(distributionsWithFloatAmounts),
        note: formData.message || '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formattedPhone,
      });

      if (!paymentResponse?.data) {
        throw new Error('Failed to create integrated payment');
      }

      const parsedPaymentData = typeof paymentResponse.data === 'string'
        ? JSON.parse(paymentResponse.data)
        : paymentResponse.data;

      if (parsedPaymentData.statusCode === 200) {
        enqueueSnackbar('התשלום נוצר בהצלחה', { variant: 'success' });
        reset();
      } else {
        enqueueSnackbar('אירעה תקלה', { variant: 'error' });
        console.error('Payment creation failed:', parsedPaymentData);
      }
    } catch (error) {
      enqueueSnackbar('אירעה תקלה', { variant: 'error' });
      console.error('Failed to submit:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      component="form"
      spacing={2}
      width={'400px'}
      alignItems={'flex-end'}
      sx={{ ml: 'auto', direction: 'rtl' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Typography sx={{ width: '100%' }} fontSize="18px" fontWeight="700" fontFamily={'AlmoniDL'} mb={2} textAlign="right">O + F</Typography>

      <Controller
        name="firstName"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="שם פרטי"
            fullWidth
            required
            error={!!errors.firstName}
            value={field.value?.toString() || ''}
            helperText={errors.firstName?.message}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="שם משפחה"
            fullWidth
            required
            error={!!errors.lastName}
            value={field.value?.toString() || ''}
            helperText={errors.lastName?.message}
          />
        )}
      />
      <Controller
        name="phone"
        control={control}
        rules={{ 
          required: 'חובה למלא את השדה',
          validate: (value) => {
            const formatted = formatIsraeliPhoneNumber(value);
            if (!formatted || !formatted.startsWith('+972')) {
              return 'מספר טלפון לא תקין. יש להזין מספר טלפון ישראלי תקין';
            }
            return true;
          }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label="טלפון"
            fullWidth
            required
            error={!!errors.phone}
            value={field.value?.toString() || ''}
            helperText={errors.phone?.message}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="דואר אלקטרוני"
            fullWidth
            required
            error={!!errors.email}
            value={field.value?.toString() || ''}
            helperText={errors.email?.message}
          />
        )}
      />
      <Controller
        name="userId"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="ת״ז"
            fullWidth
            required
            error={!!errors.userId}
            value={field.value?.toString() || ''}
            helperText={errors.userId?.message}
          />
        )}
      />
      <Controller
        name="companyId"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="ח.פ"
            fullWidth
            error={!!errors.companyId}
            value={field.value?.toString() || ''}
            helperText={errors.companyId?.message}
          />
        )}
      />
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />
      <Controller
        name="bank"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="בנק"
            fullWidth
            required
            error={!!errors.bank}
            value={field.value?.toString() || ''}
            helperText={errors.bank?.message}
          />
        )}
      />
      <Controller
        name="branch"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="סניף"
            fullWidth
            required
            error={!!errors.branch}
            value={field.value?.toString() || ''}
            helperText={errors.branch?.message}
          />
        )}
      />
      <Controller
        name="account"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="מספר חשבון"
            fullWidth
            required
            error={!!errors.account}
            value={field.value?.toString() || ''}
            helperText={errors.account?.message}
          />
        )}
      />


      <Tabs
        tabs={myTabs}
        initialTab={0}
        onChange={handleTabChange}
        contrast={false}
      />

      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />

      <Box width="100%">
        <Typography fontSize="18px" fontWeight="700" color="black" fontFamily={'AlmoniDL'} mb={2} textAlign="right">Distributions</Typography>
        {fields.map((field, index) => (
          <Box key={field.id} mb={2} py={2}>
            <Stack spacing={2} alignItems="flex-end">
              <Controller
                name={`distributions.${index}.bankAccount`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="חשבון בנק"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.bankAccount}
                    helperText={errors.distributions?.[index]?.bankAccount?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Controller
                name={`distributions.${index}.amount`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="סכום"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.amount}
                    helperText={errors.distributions?.[index]?.amount?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Controller
                name={`distributions.${index}.description`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="תיאור"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.description}
                    helperText={errors.distributions?.[index]?.description?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Button
                onClick={() => remove(index)}
                sx={{
                  backgroundColor: '#ff4444',
                  color: 'white',
                  ':hover': { backgroundColor: '#cc0000' },
                }}
              >
                הסר
              </Button>
            </Stack>
          </Box>
        ))}
        <Button
          onClick={() => append({ bankAccount: '', amount: 0, description: '' })}
          sx={{
            backgroundColor: '#4CAF50',
            color: 'white',
            ':hover': { backgroundColor: '#45a049' },
            width: '100%',
            mb: 2
          }}
        >
          הוסף
        </Button>
      </Box>

      <Button
        type="submit"
        loading={loading}
        sx={{
          backgroundColor: '#B6F829',
          borderRadius: '15px',
          ':hover': { backgroundColor: '#B6F829' },
          width: '100%',
        }}
      >
        <Typography
          fontSize="18px"
          fontWeight="700"
          color="black"
          fontFamily={'AlmoniDL'}
        >
          שליחת לינק
        </Typography>
      </Button>
    </Stack>
  );
};

export default Integrated;